import { esc } from '../escape.js';
import {
  zonedParts, zonedToDate, addDays, weekdayIndex, WEEKDAYS, dayNum, weekLabel, dayLabel,
  shortTime, hoursBetween, fmtHours, todayIn, mondayOf, clockShort, fitsWindow,
} from '../scheduleTime.js';

// "isn't available on Tuesdays" (WEEKDAYS is Mon-first short names).
const WEEKDAY_PLURALS = ['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'];

// Schedule panel (2026-10-15, free on every plan): a week builder (team down the
// side, days across, shifts colored by station) and the business's station list.
// Same shell as Timesheets (.ts-layout sidebar + drawer). Times are planned in
// the business timezone (scheduleTime.js). Drafts (published_at null) have dashed
// edges until "Publish week". Mockup: claude.ai/artifact/D8EHoxaEPjAhWWAUCUVkjH.

const AVATAR_COLORS = ['#0FA267', '#7C3AED', '#EA580C', '#2563EB', '#0E9384', '#DB2777', '#CA8A04', '#4F46E5'];
// Picked in turn for new stations, and cycled by tapping a station's swatch.
export const STATION_PALETTE = ['#0FA267', '#7C3AED', '#EA580C', '#2563EB', '#DB2777', '#0E9384', '#CA8A04', '#64748B'];
const NO_STATION_COLOR = '#64748B';
const OVERTIME_HOURS = 40;

const ICONS = {
  week: '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/>',
  stations: '<path d="m12.83 2.18 8.18 3.72a1 1 0 0 1 0 1.83l-8.18 3.72a2 2 0 0 1-1.66 0L3 7.73a1 1 0 0 1 0-1.83l8.17-3.72a2 2 0 0 1 1.66 0z"/><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/>',
  copy: '<rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/>',
  send: '<path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4Z"/>',
  requests: '<path d="M8 3 4 7l4 4"/><path d="M4 7h16"/><path d="m16 21 4-4-4-4"/><path d="M20 17H4"/>',
  availability: '<path d="M2 21a8 8 0 0 1 13.292-6"/><circle cx="10" cy="8" r="5"/><path d="m16 19 2 2 4-4"/>',
};
const LEAVE_LABEL = { vacation: '✈️ Vacation', sick: '🤒 Sick' };
const svg = (d, s = 16) => `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;

const staffName = (s) => `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || s.email || 'Staff';
const initials = (name) => name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

class ScheduleView {
  _panel   = document.querySelector('#schedulePanel');
  _sidebar = document.querySelector('#scheduleSidebar');
  _content = document.querySelector('#scheduleContent');
  _section = 'week';
  _data    = null;   // { monday, tz, staff, stations, shifts, series, availability, leave, requests }
  _handlers = {};
  _lastTimes = { start: '09:00', end: '17:00' }; // the dialog reopens with the last times used

  open() { this._panel.classList.remove('hidden', 'cashflow-exiting'); }

  close() {
    this._closeDrawer();
    this.closeDialog();
    this._panel.classList.add('cashflow-exiting');
    setTimeout(() => {
      this._panel.classList.add('hidden');
      this._panel.classList.remove('cashflow-exiting');
    }, 220);
  }

  isOpen() { return !this._panel.classList.contains('hidden'); }

  renderLoading() {
    this._content.innerHTML = `<div class="sch-loading"><span class="skel" style="height:44px;border-radius:14px"></span><span class="skel" style="height:86px;border-radius:16px"></span><span class="skel" style="height:320px;border-radius:18px"></span></div>`;
  }

  render(data) {
    this._data = data;
    this._renderSidebar();
    if (this._section === 'stations') this._renderStations();
    else if (this._section === 'requests') this._renderRequests();
    else if (this._section === 'availability') this._renderAvailability();
    else this._renderWeek();
  }

  setSection(id) { this._section = id; }

  // ── Handlers (controller callbacks) ──────────────────────────────────────────
  // onOpen, onClose, onWeek(delta | 0 = this week), onSaveShift(payload),
  // onDeleteShift(shift), onCopyLastWeek(), onPublish(), onAddStation({name,color}),
  // onUpdateStation(id, patch), onArchiveStation(id), onSection(id),
  // onReviewRequest(request, 'approve' | 'reject'), onSaveAvailability(staffId, days)
  bind(handlers) {
    this._handlers = handlers;
    document.addEventListener('click', (e) => {
      if (e.target.closest("[data-action='schedule']")) handlers.onOpen();
    });
    this._panel.querySelector('.schedule-back')?.addEventListener('click', () => handlers.onClose());
    const btn = this._panel.querySelector('#scheduleNavBtn');
    btn?.addEventListener('click', () => (btn.getAttribute('aria-expanded') === 'true' ? this._closeDrawer() : this._openDrawer()));
    this._panel.querySelector('#scheduleNavBackdrop')?.addEventListener('click', () => this._closeDrawer());
    this._content.addEventListener('click', (e) => this._onContentClick(e));
    this._content.addEventListener('change', (e) => this._onContentChange(e));
    this._content.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('.sch-station-name, #schNewStationName')) { e.preventDefault(); e.target.blur(); if (e.target.id === 'schNewStationName') this._addStationFromInput(); }
    });
  }

  // ── Mobile drawer (same pattern as Timesheets) ───────────────────────────────
  _openDrawer() {
    this._sidebar.classList.add('panel-sidebar--open');
    this._panel.querySelector('#scheduleNavBackdrop')?.classList.add('panel-nav-backdrop--visible');
    this._panel.querySelector('#scheduleNavBtn')?.setAttribute('aria-expanded', 'true');
  }

  _closeDrawer() {
    this._sidebar.classList.remove('panel-sidebar--open');
    this._panel.querySelector('#scheduleNavBackdrop')?.classList.remove('panel-nav-backdrop--visible');
    this._panel.querySelector('#scheduleNavBtn')?.setAttribute('aria-expanded', 'false');
  }

  // ── Sidebar ──────────────────────────────────────────────────────────────────
  _renderSidebar() {
    const waiting = this._pendingRequests().length;
    const nav = {
      week:         { c: '#0FA267', label: 'Schedule', desc: 'Build &amp; publish the week' },
      requests:     { c: '#2563EB', label: 'Requests', desc: 'Swaps &amp; open-shift pickups', count: waiting },
      availability: { c: '#EA580C', label: 'Availability', desc: 'When each person can work' },
      stations:     { c: '#7C3AED', label: 'Stations', desc: 'Barista, kitchen &amp; more' },
    };
    this._sidebar.innerHTML = `<p class="ts-side-label">Sections</p>${Object.entries(nav).map(([id, n]) => `
      <button class="pb-nav-item ts-tab${this._section === id ? ' ts-tab--active' : ''}" data-section="${id}" type="button" style="--nav-c:${n.c}">
        <span class="pb-nav-icon">${svg(ICONS[id], 18)}</span>
        <span class="pb-nav-text"><span class="pb-nav-name">${n.label}</span><span class="pb-nav-desc">${n.desc}</span></span>
        ${n.count ? `<span class="sch-nav-count" aria-label="${n.count} waiting">${n.count}</span>` : ''}
      </button>`).join('')}`;
    this._sidebar.querySelectorAll('.ts-tab').forEach((b) => b.addEventListener('click', () => {
      this._closeDrawer();
      if (b.dataset.section === this._section) return;
      this._section = b.dataset.section;
      this.render(this._data);
      this._handlers.onSection?.(this._section);
    }));
  }

  // ── Week ─────────────────────────────────────────────────────────────────────
  _station(id) { return this._data.stations.find((s) => s.id === id) ?? null; }
  _dayOf(iso) { return zonedParts(new Date(iso), this._data.tz).dateStr; }
  _person(id) { return this._data.staff.find((s) => s.id === id) ?? null; }
  _firstName(id) { const p = this._person(id); return p ? staffName(p).split(' ')[0] : 'They'; }
  _pendingRequests() { return (this._data?.requests ?? []).filter((r) => r.status === 'pending'); }

  // Leave covering a day: approved wins over pending.
  _leaveOn(staffId, day) {
    const list = (this._data.leave ?? []).filter((l) => l.staffId === staffId && l.startDate <= day && l.endDate >= day);
    return list.find((l) => l.status === 'approved') ?? list[0] ?? null;
  }
  _availOn(staffId, day) {
    return (this._data.availability ?? []).find((a) => a.staffId === staffId && a.weekday === weekdayIndex(day) && a.status !== 'available') ?? null;
  }
  // Why this person shouldn't work this shift, or null. kind: 'leave' | 'pending' | 'off' | 'outside'.
  _conflict(staffId, day, startTime, endTime) {
    if (!staffId) return null;
    const lv = this._leaveOn(staffId, day);
    if (lv?.status === 'approved') return { kind: 'leave', text: `${this._firstName(staffId)} is on ${lv.type === 'sick' ? 'sick leave' : 'vacation'} ${dayLabel(day)}.` };
    const av = this._availOn(staffId, day);
    const why = av?.note ? ` (${av.note})` : '';
    if (av?.status === 'unavailable') return { kind: 'off', text: `${this._firstName(staffId)} isn't available on ${WEEKDAY_PLURALS[weekdayIndex(day)]}${why}.` };
    if (av?.status === 'partial' && startTime && !fitsWindow(av.startTime, av.endTime, startTime, endTime)) {
      return { kind: 'outside', text: `${this._firstName(staffId)} is only free ${clockShort(av.startTime)}–${clockShort(av.endTime)} on ${WEEKDAY_PLURALS[weekdayIndex(day)]}${why}.` };
    }
    if (lv) return { kind: 'pending', text: `${this._firstName(staffId)} has asked for leave on ${dayLabel(day)} (not decided yet).` };
    return null;
  }

  _renderWeek() {
    const { monday, tz, staff, shifts } = this._data;
    const days = [...Array(7)].map((_, i) => addDays(monday, i));
    const today = todayIn(tz);
    const isThisWeek = mondayOf(today) === monday;
    const drafts = shifts.filter((s) => !s.publishedAt).length;
    const open = shifts.filter((s) => !s.staffId);
    const totalH = shifts.reduce((h, s) => h + hoursBetween(s.startsAt, s.endsAt), 0);

    // Rows: active staff, plus anyone deactivated who still has a shift this week.
    const rows = staff.filter((s) => s.isActive !== false);
    shifts.forEach((sh) => {
      if (sh.staffId && !rows.some((r) => r.id === sh.staffId)) {
        const s = staff.find((x) => x.id === sh.staffId);
        rows.push(s ?? { id: sh.staffId, firstName: 'Former', lastName: 'staff', isActive: false });
      }
    });
    const peopleScheduled = new Set(shifts.filter((s) => s.staffId).map((s) => s.staffId)).size;

    const status = !shifts.length
      ? `<span class="sch-status sch-status--empty">Nothing scheduled yet</span>`
      : drafts
        ? `<span class="sch-status sch-status--draft">Draft · ${drafts} unpublished ${drafts === 1 ? 'shift' : 'shifts'}</span>`
        : `<span class="sch-status sch-status--live">Published</span>`;

    const tile = (accent, label, val, sub) => `
      <div class="ts-stat-tile" style="--tile-accent:${accent}">
        <div class="ts-stat-tile-label">${label}</div>
        <div class="ts-stat-tile-val">${val}</div>
        <div class="ts-stat-tile-sub">${sub}</div>
      </div>`;

    const openReqs = (this._data.requests ?? []).filter((r) => r.status === 'pending' || r.status === 'waiting_teammate');
    const chip = (sh) => {
      const st = this._station(sh.stationId);
      const ovn = this._dayOf(sh.endsAt) !== this._dayOf(sh.startsAt) ? ' <span class="sch-shift-ovn" title="Ends the next day">+1</span>' : '';
      const clash = this._conflict(sh.staffId, this._dayOf(sh.startsAt), zonedParts(new Date(sh.startsAt), tz).timeStr, zonedParts(new Date(sh.endsAt), tz).timeStr);
      const bad = clash && clash.kind !== 'pending';
      const asks = openReqs.filter((r) => r.shiftId === sh.id || r.targetShiftId === sh.id);
      const tag = !asks.length ? ''
        : sh.staffId ? ' <span class="sch-shift-req" title="Swap requested">⇄</span>'
        : ` <span class="sch-shift-req" title="${asks.length} asked to pick this up">✋${asks.length}</span>`;
      const title = [clash?.text, sh.note].filter(Boolean).join(' · ');
      return `<button type="button" class="sch-shift${sh.publishedAt ? '' : ' sch-shift--draft'}${sh.staffId ? '' : ' sch-shift--open'}${bad ? ' sch-shift--clash' : ''}" data-shift="${esc(sh.id)}" style="--sc:${st?.color ?? NO_STATION_COLOR}" title="${esc(title)}">
        <b>${sh.seriesId ? '<span class="sch-shift-rep" title="Repeats weekly">↻</span> ' : ''}${shortTime(new Date(sh.startsAt), tz)}–${shortTime(new Date(sh.endsAt), tz)}${ovn}${tag}</b>
        <span>${bad ? '⚠️ ' : ''}${esc(st?.name ?? 'No station')}${sh.note ? ' · 📝' : ''}</span>
      </button>`;
    };
    // A person's day shows approved/pending leave or their availability above the shifts.
    const dayNote = (staffId, day) => {
      if (!staffId) return '';
      const lv = this._leaveOn(staffId, day);
      if (lv) return `<span class="sch-off sch-off--${lv.status === 'approved' ? 'leave' : 'pending'}">${lv.status === 'approved' ? LEAVE_LABEL[lv.type] ?? 'On leave' : 'Leave asked'}</span>`;
      const av = this._availOn(staffId, day);
      if (av?.status === 'unavailable') return `<span class="sch-off sch-off--unavail" title="${esc(av.note || '')}">Unavailable</span>`;
      if (av?.status === 'partial') return `<span class="sch-off sch-off--partial" title="${esc(av.note || '')}">Free ${clockShort(av.startTime)}–${clockShort(av.endTime)}</span>`;
      return '';
    };
    const cell = (staffId, day) => {
      const list = shifts.filter((s) => (s.staffId ?? null) === staffId && this._dayOf(s.startsAt) === day);
      const label = `Add a shift on ${dayLabel(day)}`;
      const lv = staffId ? this._leaveOn(staffId, day) : null;
      const off = lv?.status === 'approved' || (staffId && this._availOn(staffId, day)?.status === 'unavailable');
      return `<div class="sch-cell${day === today ? ' sch-cell--today' : ''}${list.length ? '' : ' sch-cell--empty'}${off ? ' sch-cell--off' : ''}">
        ${dayNote(staffId, day)}
        ${list.map(chip).join('')}
        <button type="button" class="sch-add" data-add-staff="${esc(staffId ?? '')}" data-add-day="${day}" aria-label="${esc(label)}" title="${esc(label)}">+</button>
      </div>`;
    };
    const rowHours = (staffId) => shifts.filter((s) => (s.staffId ?? null) === staffId).reduce((h, s) => h + hoursBetween(s.startsAt, s.endsAt), 0);

    const head = `<div class="sch-gh sch-gh--team">Team</div>${days.map((d, i) => `
      <div class="sch-gh${d === today ? ' sch-gh--today' : ''}">${WEEKDAYS[i]}<b>${dayNum(d)}</b></div>`).join('')}`;
    const body = rows.map((s, i) => {
      const name = staffName(s), h = rowHours(s.id);
      return `<div class="sch-who">
          <span class="sch-av" style="--ac:${AVATAR_COLORS[i % AVATAR_COLORS.length]}">${esc(initials(name))}</span>
          <span class="sch-who-text"><b>${esc(name)}</b><span>${esc(s.role ?? '')}${s.role ? ' · ' : ''}<span class="${h > OVERTIME_HOURS ? 'sch-over' : ''}">${fmtHours(h)}</span></span></span>
        </div>${days.map((d) => cell(s.id, d)).join('')}`;
    }).join('');
    const openRow = `<div class="sch-who sch-who--open">
        <span class="sch-av sch-av--open">?</span>
        <span class="sch-who-text"><b>Open shifts</b><span>Not assigned yet</span></span>
      </div>${days.map((d) => cell(null, d)).join('')}`;
    const foot = `<div class="sch-foot sch-foot--label">Hours / day</div>${days.map((d) => {
      const h = shifts.filter((s) => this._dayOf(s.startsAt) === d).reduce((t, s) => t + hoursBetween(s.startsAt, s.endsAt), 0);
      return `<div class="sch-foot">${h ? fmtHours(h) : '–'}</div>`;
    }).join('')}`;

    const pendingCount = this._pendingRequests().length;
    const usedStations = this._data.stations.filter((st) => !st.archived || shifts.some((s) => s.stationId === st.id));
    this._content.innerHTML = `
      <div class="sch-toolbar">
        <div class="sch-week-nav">
          <button type="button" data-week="-1" aria-label="Previous week">‹</button>
          <span class="sch-week-label">${weekLabel(monday)}</span>
          <button type="button" data-week="1" aria-label="Next week">›</button>
        </div>
        ${isThisWeek ? '' : '<button type="button" class="sch-btn sch-btn--ghost" data-week="0">This week</button>'}
        ${status}
        <span class="sch-spacer"></span>
        <button type="button" class="sch-btn" id="schCopyBtn">${svg(ICONS.copy)}Copy last week</button>
        <button type="button" class="sch-btn sch-btn--primary" id="schPublishBtn"${drafts ? '' : ' disabled'}>${svg(ICONS.send)}Publish week</button>
      </div>
      <div class="ts-stat-strip sch-stats">
        ${tile('#18AE79', 'Scheduled', fmtHours(totalH), `${shifts.length} ${shifts.length === 1 ? 'shift' : 'shifts'}`)}
        ${tile('#2563EB', 'Open shifts', open.length, open.length ? 'waiting for someone' : 'everything is covered')}
        ${tile('#F59E0B', 'Unpublished', drafts, drafts ? 'staff can\'t see these yet' : 'staff see everything')}
        ${tile('#7C3AED', 'Team scheduled', `${peopleScheduled} / ${rows.filter((r) => r.isActive !== false).length}`, 'people this week')}
      </div>
      <div class="sch-grid-wrap"><div class="sch-grid">${head}${body}${openRow}${foot}</div></div>
      <div class="sch-legend">
        ${usedStations.map((st) => `<span><i style="--sc:${st.color}"></i>${esc(st.name)}</span>`).join('')}
        <span><i class="sch-legend-draft"></i>Dashed = not published yet</span>
        <span><i class="sch-legend-off"></i>Off (leave or unavailable)</span>
        ${pendingCount ? `<button type="button" class="sch-legend-req" data-section-go="requests">✋ ${pendingCount} ${pendingCount === 1 ? 'request needs' : 'requests need'} your OK</button>` : ''}
      </div>`;
  }

  // ── Shift dialog ─────────────────────────────────────────────────────────────
  // shift = an existing shift to edit, or { staffId, day } to add one.
  // Days are chips (several = one shift on each, "a week straight" in one go).
  // Repeat "Every week" turns the chosen weekdays into a repeating rule; editing
  // a repeat asks This / Following / All upcoming in the controller.
  openDialog(shift) {
    this.closeDialog();
    const { monday, tz, staff } = this._data;
    const editing = !!shift.id;
    const series = shift.seriesId ? this._data.series.find((x) => x.id === shift.seriesId) ?? null : null;
    const days = [...Array(7)].map((_, i) => addDays(monday, i));
    const ownDay = editing ? this._dayOf(shift.startsAt) : shift.day;
    const chosen = new Set(series ? days.filter((d, i) => series.weekdays.includes(i)) : [ownDay]);
    chosen.add(ownDay);
    const start = editing ? zonedParts(new Date(shift.startsAt), tz).timeStr : this._lastTimes.start;
    const end = editing ? zonedParts(new Date(shift.endsAt), tz).timeStr : this._lastTimes.end;
    const staffId = shift.staffId ?? '';
    const stationId = editing ? (shift.stationId ?? '') : (this._lastStation ?? this._data.stations.find((s) => !s.archived)?.id ?? '');
    const people = staff.filter((s) => s.isActive !== false || s.id === staffId);
    const stations = this._data.stations.filter((s) => !s.archived || s.id === stationId);
    const until = series?.untilDate ?? '';

    const pill = series ? '<span class="sch-status sch-status--repeat">↻ Repeats weekly</span>'
      : editing && shift.publishedAt ? '<span class="sch-status sch-status--live">Published</span>'
      : editing ? '<span class="sch-status sch-status--draft">Draft</span>' : '';

    const el = document.createElement('div');
    el.className = 'modal-backdrop sch-dialog-backdrop';
    el.id = 'schDialog';
    el.innerHTML = `
      <div class="sch-dialog" role="dialog" aria-modal="true" aria-labelledby="schDialogTitle">
        <div class="sch-dialog-head">
          <h3 id="schDialogTitle">${editing ? 'Edit shift' : 'Add shift'}</h3>
          ${pill}
          <button type="button" class="modal-close sch-dialog-close" aria-label="Close">&times;</button>
        </div>
        <label class="sch-field"><span>Who</span>
          <select id="schWho">
            <option value="">Open shift (anyone can take it)</option>
            ${people.map((s) => `<option value="${esc(s.id)}"${s.id === staffId ? ' selected' : ''}>${esc(staffName(s))}</option>`).join('')}
          </select>
        </label>
        <div class="sch-field"><span class="sch-field-label-row">Days
          <span class="sch-presets">
            <button type="button" data-preset="weekdays">Weekdays</button>
            <button type="button" data-preset="weekends">Weekends</button>
            <button type="button" data-preset="all">Every day</button>
          </span></span>
          <div class="sch-days" role="group" aria-label="Days">
            ${days.map((d, i) => `<button type="button" class="sch-day${chosen.has(d) ? ' sch-day--on' : ''}" data-day="${d}" aria-pressed="${chosen.has(d)}"><span>${WEEKDAYS[i].slice(0, 2)}</span><b>${dayNum(d)}</b></button>`).join('')}
          </div>
        </div>
        <div class="sch-field-row sch-field-row--2">
          <label class="sch-field"><span>Start</span><input type="time" id="schStart" value="${start}" required /></label>
          <label class="sch-field"><span>End</span><input type="time" id="schEnd" value="${end}" required /></label>
        </div>
        <div class="sch-field-row sch-field-row--2">
          <label class="sch-field"><span>Repeat</span>
            <select id="schRepeat">
              <option value="none"${series ? '' : ' selected'}>Doesn't repeat</option>
              <option value="weekly"${series ? ' selected' : ''}>Every week</option>
            </select>
          </label>
          <label class="sch-field" id="schEndsField"${series ? '' : ' hidden'}><span>Ends</span>
            <select id="schEnds">
              <option value="never"${until ? '' : ' selected'}>Never</option>
              <option value="on"${until ? ' selected' : ''}>On a date</option>
            </select>
          </label>
        </div>
        <label class="sch-field" id="schUntilField"${until ? '' : ' hidden'}><span>Last day</span>
          <input type="date" id="schUntil" value="${until}" min="${ownDay}" />
        </label>
        <div class="sch-field"><span>Station</span>
          <div class="sch-station-pick" role="radiogroup" aria-label="Station">
            ${stations.map((st) => `<button type="button" role="radio" aria-checked="${st.id === stationId}" class="sch-pick${st.id === stationId ? ' sch-pick--on' : ''}" data-pick="${esc(st.id)}" style="--sc:${st.color}"><i></i>${esc(st.name)}</button>`).join('')}
            <button type="button" role="radio" aria-checked="${!stationId}" class="sch-pick${stationId ? '' : ' sch-pick--on'}" data-pick="" style="--sc:${NO_STATION_COLOR}"><i></i>None</button>
          </div>
        </div>
        <label class="sch-field"><span>Note for staff <em>(optional)</em></span>
          <input type="text" id="schNote" maxlength="200" placeholder="e.g. Open the store, receive milk delivery" value="${esc(shift.note ?? '')}" />
        </label>
        <div class="sch-dialog-info" id="schDialogInfo" aria-live="polite"></div>
        <div class="sch-dialog-actions">
          ${editing ? '<button type="button" class="sch-btn sch-btn--danger" id="schDeleteShift">Delete</button>' : ''}
          <span class="sch-spacer"></span>
          <button type="button" class="sch-btn" id="schCancelShift">Cancel</button>
          <button type="button" class="sch-btn sch-btn--primary" id="schSaveShift">${editing ? 'Save' : 'Add shift'}</button>
        </div>
      </div>`;
    document.body.appendChild(el);
    this._dialog = { el, shift, series, stationId, days, ownDay };

    const update = () => this._updateDialogInfo();
    const repeatOn = () => el.querySelector('#schRepeat').value === 'weekly';
    const setDay = (btn, on) => { btn.classList.toggle('sch-day--on', on); btn.setAttribute('aria-pressed', String(on)); };
    el.querySelectorAll('select, input').forEach((i) => i.addEventListener('input', update));
    el.querySelector('#schRepeat').addEventListener('input', () => {
      el.querySelector('#schEndsField').hidden = !repeatOn();
      el.querySelector('#schUntilField').hidden = !repeatOn() || el.querySelector('#schEnds').value !== 'on';
      // Editing a single one-off shift: without a repeat it lives on one day only.
      if (!repeatOn() && editing && !series) el.querySelectorAll('.sch-day').forEach((b) => setDay(b, b.dataset.day === ownDay));
      update();
    });
    el.querySelector('#schEnds').addEventListener('input', () => {
      const on = el.querySelector('#schEnds').value === 'on';
      el.querySelector('#schUntilField').hidden = !on;
      if (on && !el.querySelector('#schUntil').value) el.querySelector('#schUntil').value = addDays(monday, 6 + 28);
      update();
    });
    el.querySelectorAll('.sch-day').forEach((b) => b.addEventListener('click', () => {
      // A single one-off being edited moves between days; otherwise days toggle.
      if (editing && !series && !repeatOn()) el.querySelectorAll('.sch-day').forEach((x) => setDay(x, x === b));
      else {
        const on = !b.classList.contains('sch-day--on');
        if (!on && el.querySelectorAll('.sch-day--on').length === 1) return; // keep at least one
        setDay(b, on);
      }
      update();
    }));
    el.querySelectorAll('[data-preset]').forEach((p) => p.addEventListener('click', () => {
      const pick = { weekdays: [0, 1, 2, 3, 4], weekends: [5, 6], all: [0, 1, 2, 3, 4, 5, 6] }[p.dataset.preset];
      if (editing && !series && !repeatOn()) { // editing one shift: presets switch to "repeat"
        el.querySelector('#schRepeat').value = 'weekly';
        el.querySelector('#schRepeat').dispatchEvent(new Event('input'));
      }
      el.querySelectorAll('.sch-day').forEach((b, i) => setDay(b, pick.includes(i)));
      update();
    }));
    el.querySelector('.sch-dialog-close').addEventListener('click', () => this.closeDialog());
    el.querySelector('#schCancelShift').addEventListener('click', () => this.closeDialog());
    el.querySelectorAll('.sch-pick').forEach((b) => b.addEventListener('click', () => {
      this._dialog.stationId = b.dataset.pick;
      el.querySelectorAll('.sch-pick').forEach((x) => {
        const on = x === b;
        x.classList.toggle('sch-pick--on', on);
        x.setAttribute('aria-checked', String(on));
      });
    }));
    el.querySelector('#schSaveShift').addEventListener('click', () => this._submitDialog());
    el.querySelector('#schDeleteShift')?.addEventListener('click', () => this._handlers.onDeleteShift?.(shift));
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeDialog();
      if (e.key === 'Enter' && e.target.matches('input:not([type="date"])')) { e.preventDefault(); this._submitDialog(); }
    });
    update();
    el.querySelector(editing ? '#schStart' : '#schWho').focus();
  }

  closeDialog() {
    this._dialog?.el.remove();
    this._dialog = null;
  }

  setDialogBusy(busy) {
    this._dialog?.el.querySelectorAll('button, select, input').forEach((b) => { b.disabled = busy; });
  }

  // The form as plain values: { days[], startTime, endTime, hours, overnight, repeat, untilDate }.
  _readForm() {
    const el = this._dialog.el;
    const startTime = el.querySelector('#schStart').value;
    const endTime = el.querySelector('#schEnd').value;
    const days = [...el.querySelectorAll('.sch-day--on')].map((b) => b.dataset.day);
    if (!startTime || !endTime || startTime === endTime || !days.length) return null;
    const tz = this._data.tz;
    const first = days[0];
    const startsAt = zonedToDate(first, startTime, tz);
    const endsAt = zonedToDate(endTime > startTime ? first : addDays(first, 1), endTime, tz);
    const repeat = el.querySelector('#schRepeat').value === 'weekly';
    const untilDate = repeat && el.querySelector('#schEnds').value === 'on' ? (el.querySelector('#schUntil').value || null) : null;
    return { days, startTime, endTime, hours: hoursBetween(startsAt, endsAt), overnight: endTime < startTime, repeat, untilDate };
  }

  _updateDialogInfo() {
    const el = this._dialog.el;
    const info = el.querySelector('#schDialogInfo');
    const f = this._readForm();
    if (!f) { info.innerHTML = `<p class="sch-info">Pick at least one day and a start and end time.</p>`; return; }
    const tz = this._data.tz;
    const who = el.querySelector('#schWho').value;
    const { shift, series } = this._dialog;
    // This shift (and, for a repeat, its siblings this week) are what we're replacing.
    const others = this._data.shifts.filter((s) => who && s.staffId === who && s.id !== shift.id && !(series && s.seriesId === series.id));
    const weekH = others.reduce((h, s) => h + hoursBetween(s.startsAt, s.endsAt), 0) + f.hours * f.days.length;
    const person = this._data.staff.find((s) => s.id === who);
    const first = person ? staffName(person).split(' ')[0] : '';
    // "every day except Wed" reads better than listing six days (Kelly, 2026-10-15).
    const picked = f.days.map(weekdayIndex);
    const missing = WEEKDAYS.filter((_, i) => !picked.includes(i));
    const isWeekdays = picked.length === 5 && missing.join() === 'Sat,Sun';
    const isWeekends = picked.length === 2 && picked.includes(5) && picked.includes(6);
    const dayNames = picked.length === 7 ? 'day'
      : isWeekdays ? 'weekday'
      : isWeekends ? 'weekend day'
      : picked.length >= 5 ? `day except ${missing.join(' and ')}`
      : f.days.map((d) => WEEKDAYS[weekdayIndex(d)]).join(', ');
    const what = f.repeat
      ? `Repeats every ${dayNames} · ${f.untilDate ? `until ${dayLabel(f.untilDate)}` : 'no end date'}`
      : f.days.length > 1 ? `${f.days.length} shifts this week (every ${dayNames})` : '';
    const lines = [`<p class="sch-info">${fmtHours(f.hours)} each${f.overnight ? ' · ends the next day' : ''}${who ? ` · ${esc(first)} at ${fmtHours(weekH)} this week` : ''}</p>`];
    if (what) lines.push(`<p class="sch-info sch-info--strong">${what}</p>`);
    const clash = f.days.map((d) => {
      const s0 = zonedToDate(d, f.startTime, tz), e0 = zonedToDate(f.endTime > f.startTime ? d : addDays(d, 1), f.endTime, tz);
      return others.find((s) => new Date(s.startsAt) < e0 && new Date(s.endsAt) > s0);
    }).find(Boolean);
    if (clash) lines.push(`<p class="sch-warn">⚠️ Overlaps ${esc(first)}'s ${dayLabel(this._dayOf(clash.startsAt))} ${shortTime(new Date(clash.startsAt), tz)}–${shortTime(new Date(clash.endsAt), tz)} shift.</p>`);
    if (who && weekH > OVERTIME_HOURS) lines.push(`<p class="sch-warn">⚠️ This puts ${esc(first)} over ${OVERTIME_HOURS} hours this week.</p>`);
    // Leave + availability (phase 2): one line per distinct problem.
    if (who) {
      const seen = new Set();
      f.days.forEach((d) => {
        const c = this._conflict(who, d, f.startTime, f.endTime);
        if (!c || seen.has(c.text)) return;
        seen.add(c.text);
        lines.push(`<p class="${c.kind === 'pending' ? 'sch-info sch-info--strong' : 'sch-warn'}">${c.kind === 'pending' ? 'ℹ️' : '⚠️'} ${esc(c.text)}</p>`);
      });
    }
    info.innerHTML = lines.join('');
  }

  _submitDialog() {
    const el = this._dialog.el;
    const f = this._readForm();
    if (!f) { el.querySelector('#schEnd').focus(); return; }
    if (f.repeat && el.querySelector('#schEnds').value === 'on' && !f.untilDate) { el.querySelector('#schUntil').focus(); return; }
    this._lastTimes = { start: f.startTime, end: f.endTime };
    this._lastStation = this._dialog.stationId || null;
    this._handlers.onSaveShift?.({
      shift: this._dialog.shift.id ? this._dialog.shift : null,
      staffId: el.querySelector('#schWho').value || null,
      stationId: this._dialog.stationId || null,
      days: f.days,
      weekdays: f.days.map(weekdayIndex),
      startTime: f.startTime,
      endTime: f.endTime,
      repeat: f.repeat,
      untilDate: f.untilDate,
      note: el.querySelector('#schNote').value,
    });
  }

  // ── "Which shifts?" chooser (repeats, publishing) ────────────────────────────
  // options: [{ value, label, hint }]; checkbox: { label, showFor: [values] } (optional).
  // Resolves { value, checked } or null when cancelled.
  askChoice({ title, body = '', options, checkbox = null, confirmLabel = 'OK', danger = false }) {
    return new Promise((resolve) => {
      const el = document.createElement('div');
      el.className = 'modal-backdrop sch-dialog-backdrop sch-choice-backdrop';
      el.innerHTML = `
        <div class="sch-dialog sch-choice" role="dialog" aria-modal="true" aria-labelledby="schChoiceTitle">
          <div class="sch-dialog-head">
            <h3 id="schChoiceTitle">${esc(title)}</h3>
            <button type="button" class="modal-close sch-dialog-close" aria-label="Close">&times;</button>
          </div>
          ${body ? `<p class="sch-choice-body">${body}</p>` : ''}
          <div class="sch-choice-list" role="radiogroup">
            ${options.map((o, i) => `
              <label class="sch-choice-opt">
                <input type="radio" name="schChoice" value="${esc(o.value)}"${i === 0 ? ' checked' : ''} />
                <span><b>${esc(o.label)}</b>${o.hint ? `<small>${esc(o.hint)}</small>` : ''}</span>
              </label>`).join('')}
          </div>
          ${checkbox ? `<label class="sch-choice-check" id="schChoiceCheck"><input type="checkbox" id="schChoiceBox" /><span>${esc(checkbox.label)}</span></label>` : ''}
          <div class="sch-dialog-actions">
            <span class="sch-spacer"></span>
            <button type="button" class="sch-btn" data-choice-cancel>Cancel</button>
            <button type="button" class="sch-btn ${danger ? 'sch-btn--danger-solid' : 'sch-btn--primary'}" data-choice-ok>${esc(confirmLabel)}</button>
          </div>
        </div>`;
      document.body.appendChild(el);
      const done = (v) => { el.remove(); resolve(v); };
      const sel = () => el.querySelector('input[name="schChoice"]:checked')?.value;
      const syncCheck = () => {
        const c = el.querySelector('#schChoiceCheck');
        if (c) c.hidden = !(checkbox.showFor ?? []).includes(sel());
      };
      el.querySelectorAll('input[name="schChoice"]').forEach((r) => r.addEventListener('change', syncCheck));
      syncCheck();
      el.querySelector('.sch-dialog-close').addEventListener('click', () => done(null));
      el.querySelector('[data-choice-cancel]').addEventListener('click', () => done(null));
      el.querySelector('[data-choice-ok]').addEventListener('click', () => {
        const box = el.querySelector('#schChoiceBox');
        done({ value: sel(), checked: !!(box && !el.querySelector('#schChoiceCheck').hidden && box.checked) });
      });
      el.addEventListener('keydown', (e) => { if (e.key === 'Escape') done(null); });
      el.querySelector('[data-choice-ok]').focus();
    });
  }

  // ── Requests (phase 3): swaps + open-shift pickups ───────────────────────────
  // Staff file them on the Time Clock. A swap waits for the teammate first, then
  // lands here; approving moves the shift(s) (review_shift_request RPC).
  _renderRequests() {
    const { tz } = this._data;
    const reqs = this._data.requests ?? [];
    const now = new Date();
    const pending = reqs.filter((r) => r.status === 'pending');
    const waiting = reqs.filter((r) => r.status === 'waiting_teammate');
    const closed = reqs.filter((r) => r.status !== 'pending' && r.status !== 'waiting_teammate');
    const name = (id, fallback) => { const p = this._person(id); return p ? staffName(p) : (fallback || 'Former staff'); };
    const first = (id, fallback) => name(id, fallback).split(' ')[0];
    const kindOf = (r) => r.kind === 'pickup'
      ? { label: 'Open shift', c: '#2563EB' }
      : r.targetShiftId ? { label: 'Trade', c: '#7C3AED' } : { label: 'Cover', c: '#0E9384' };
    const ago = (iso) => {
      const m = Math.max(0, Math.round((now - new Date(iso)) / 60000));
      return m < 60 ? `${m || 1}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
    };
    // One shift moving to its new owner.
    const move = (sh, toId, toName) => {
      if (!sh) return '<div class="sch-req-shift sch-req-shift--gone">This shift was removed</div>';
      const st = this._station(sh.stationId);
      return `<div class="sch-req-shift" style="--sc:${st?.color ?? NO_STATION_COLOR}">
        <b>${esc(dayLabel(this._dayOf(sh.startsAt)))} · ${shortTime(new Date(sh.startsAt), tz)}–${shortTime(new Date(sh.endsAt), tz)}</b>
        <span>${esc(st?.name ?? 'No station')} · ${fmtHours(hoursBetween(sh.startsAt, sh.endsAt))}</span>
        <em>${sh.staffId ? `${esc(first(sh.staffId))} → ` : 'Open → '}${esc(first(toId, toName))}</em>
      </div>`;
    };
    const warnings = (r) => {
      const out = [];
      const check = (sh, who) => {
        if (!sh || !who) return;
        const c = this._conflict(who, this._dayOf(sh.startsAt), zonedParts(new Date(sh.startsAt), tz).timeStr, zonedParts(new Date(sh.endsAt), tz).timeStr);
        if (c) out.push(c);
      };
      check(r.shift, r.kind === 'pickup' ? r.requesterId : r.targetId);
      if (r.targetShift) check(r.targetShift, r.requesterId);
      if (r.kind === 'pickup') {
        const others = pending.filter((x) => x.shiftId === r.shiftId && x.id !== r.id).length;
        if (others) out.push({ kind: 'pending', text: `${others + 1} people asked for this shift. Approving one closes the others.` });
      }
      return out.map((c) => `<p class="${c.kind === 'pending' ? 'sch-info' : 'sch-warn'}">${c.kind === 'pending' ? 'ℹ️' : '⚠️'} ${esc(c.text)}</p>`).join('');
    };
    const title = (r) => {
      const a = `<b>${esc(name(r.requesterId, r.requesterName))}</b>`, b = `<b>${esc(name(r.targetId, r.targetName))}</b>`;
      if (r.kind === 'pickup') return `${a} wants to pick up this open shift`;
      return r.targetShiftId ? `${a} and ${b} want to trade shifts` : `${a} asked ${b} to cover a shift`;
    };
    const card = (r, withActions) => {
      const k = kindOf(r);
      const started = r.shift && new Date(r.shift.startsAt) <= now;
      const sub = r.kind === 'swap'
        ? (r.status === 'pending' ? `${esc(first(r.targetId, r.targetName))} said yes` : `Waiting for ${esc(first(r.targetId, r.targetName))} to answer`)
        : '';
      return `<article class="sch-req" style="--rc:${k.c}">
        <div class="sch-req-head">
          <span class="sch-req-kind">${k.label}</span>
          ${sub ? `<span class="sch-req-sub">${sub}</span>` : ''}
          <span class="sch-req-age">${ago(r.createdAt)}</span>
        </div>
        <p class="sch-req-title">${title(r)}</p>
        <div class="sch-req-shifts">
          ${move(r.shift, r.kind === 'pickup' ? r.requesterId : r.targetId, r.kind === 'pickup' ? r.requesterName : r.targetName)}
          ${r.targetShiftId ? move(r.targetShift, r.requesterId, r.requesterName) : ''}
        </div>
        ${r.note ? `<p class="sch-req-note">“${esc(r.note)}”</p>` : ''}
        ${withActions ? warnings(r) : ''}
        ${withActions ? `<div class="sch-req-actions">
          ${started ? '<span class="sch-req-expired">This shift already started</span>' : ''}
          <span class="sch-spacer"></span>
          <button type="button" class="sch-btn" data-review="reject" data-req="${esc(r.id)}">Decline</button>
          <button type="button" class="sch-btn sch-btn--primary" data-review="approve" data-req="${esc(r.id)}"${started ? ' disabled' : ''}>Approve</button>
        </div>` : ''}
      </article>`;
    };
    const STATUS = {
      approved: ['Approved', 'live'], rejected: ['Declined by a manager', 'no'],
      declined: ['Teammate said no', 'no'], cancelled: ['Cancelled', 'muted'],
    };
    const historyRow = (r) => {
      const [label, tone] = STATUS[r.status] ?? [r.status, 'muted'];
      const sh = r.shift;
      const by = r.status === 'approved' || r.status === 'rejected' ? (r.reviewedByName ? ` by ${esc(r.reviewedByName)}` : '') : '';
      return `<div class="sch-req-row">
        <span class="sch-req-dot" style="--rc:${kindOf(r).c}"></span>
        <span class="sch-req-row-main"><b>${esc(kindOf(r).label)}</b> · ${esc(first(r.requesterId, r.requesterName))}${r.kind === 'swap' ? ` → ${esc(first(r.targetId, r.targetName))}` : ''}
          <small>${sh ? `${esc(dayLabel(this._dayOf(sh.startsAt)))} ${shortTime(new Date(sh.startsAt), tz)}–${shortTime(new Date(sh.endsAt), tz)}` : 'Shift removed'}${r.reviewNote ? ` · ${esc(r.reviewNote)}` : ''}</small></span>
        <span class="sch-req-pill sch-req-pill--${tone}">${label}${r.status === 'approved' ? by : ''}</span>
      </div>`;
    };
    this._content.innerHTML = `
      <div class="sch-reqs">
        <div class="sch-stations-head">
          <h3>Requests</h3>
          <p>Your team asks to pick up open shifts or swap with each other on the Time Clock. A swap comes here once the teammate says yes. Nothing changes on the schedule until you approve.</p>
        </div>
        <section class="sch-req-group">
          <h4>Needs your OK <span class="sch-req-count">${pending.length}</span></h4>
          ${pending.length ? pending.map((r) => card(r, true)).join('') : '<p class="sch-empty sch-req-empty">Nothing to approve right now.</p>'}
        </section>
        ${waiting.length ? `<section class="sch-req-group">
          <h4>Waiting on a teammate <span class="sch-req-count">${waiting.length}</span></h4>
          ${waiting.map((r) => card(r, false)).join('')}
        </section>` : ''}
        ${closed.length ? `<details class="sch-req-history">
          <summary>Recent <span class="sch-req-count">${closed.length}</span></summary>
          ${closed.map(historyRow).join('')}
        </details>` : ''}
      </div>`;
  }

  // ── Availability (phase 2) ───────────────────────────────────────────────────
  _availRow(staffId) {
    return [...Array(7)].map((_, i) => this._data.availability.find((a) => a.staffId === staffId && a.weekday === i)
      ?? { staffId, weekday: i, status: 'available', startTime: '', endTime: '', note: '' });
  }

  _renderAvailability() {
    const people = this._data.staff.filter((s) => s.isActive !== false);
    const pill = (a) => {
      const note = a.note ? ` title="${esc(a.note)}"` : '';
      if (a.status === 'unavailable') return `<span class="sch-av-pill sch-av-pill--off"${note}>Off${a.note ? ' 📝' : ''}</span>`;
      if (a.status === 'partial') return `<span class="sch-av-pill sch-av-pill--part"${note}>${clockShort(a.startTime)}–${clockShort(a.endTime)}${a.note ? ' 📝' : ''}</span>`;
      return `<span class="sch-av-pill"${note}>All day</span>`;
    };
    this._content.innerHTML = `
      <div class="sch-avail">
        <div class="sch-stations-head">
          <h3>Availability</h3>
          <p>When each person can work, week to week. Your team sets their own on the Time Clock (My availability), and you can change anyone's here. The schedule shows it on the grid and warns you when a shift doesn't fit.</p>
        </div>
        <div class="sch-grid-wrap"><div class="sch-avgrid">
          <div class="sch-gh sch-gh--team">Team</div>
          ${WEEKDAYS.map((d) => `<div class="sch-gh">${d}</div>`).join('')}
          <div class="sch-gh"></div>
          ${people.map((s, i) => {
            const n = staffName(s);
            return `<div class="sch-who">
                <span class="sch-av" style="--ac:${AVATAR_COLORS[i % AVATAR_COLORS.length]}">${esc(initials(n))}</span>
                <span class="sch-who-text"><b>${esc(n)}</b><span>${esc(s.role ?? '')}</span></span>
              </div>${this._availRow(s.id).map((a) => `<div class="sch-av-cell">${pill(a)}</div>`).join('')}
              <div class="sch-av-cell sch-av-cell--edit"><button type="button" class="sch-btn sch-btn--ghost" data-avail-edit="${esc(s.id)}" aria-label="Edit ${esc(n)}'s availability">Edit</button></div>`;
          }).join('') || '<p class="sch-empty">No team members yet.</p>'}
        </div></div>
      </div>`;
  }

  openAvailabilityDialog(staffId) {
    this.closeDialog();
    const person = this._person(staffId);
    if (!person) return;
    const rows = this._availRow(staffId);
    const el = document.createElement('div');
    el.className = 'modal-backdrop sch-dialog-backdrop';
    el.id = 'schDialog';
    el.innerHTML = `
      <div class="sch-dialog sch-dialog--wide" role="dialog" aria-modal="true" aria-labelledby="schAvTitle">
        <div class="sch-dialog-head">
          <h3 id="schAvTitle">${esc(staffName(person))}'s availability</h3>
          <button type="button" class="modal-close sch-dialog-close" aria-label="Close">&times;</button>
        </div>
        <p class="sch-choice-body">Repeats every week. Days left on "All day" are open for any shift.</p>
        <div class="sch-av-edit">
          ${rows.map((a) => `
            <div class="sch-av-edit-row" data-wd="${a.weekday}">
              <b>${WEEKDAYS[a.weekday]}</b>
              <div class="sch-seg" role="radiogroup" aria-label="${WEEKDAYS[a.weekday]}">
                ${[['available', 'All day'], ['partial', 'Some hours'], ['unavailable', 'Off']].map(([v, l]) => `
                  <button type="button" role="radio" class="sch-seg-btn${a.status === v ? ' sch-seg-btn--on' : ''}" data-st="${v}" aria-checked="${a.status === v}">${l}</button>`).join('')}
              </div>
              <span class="sch-av-times"${a.status === 'partial' ? '' : ' hidden'}>
                <input type="time" data-f="start" value="${a.startTime || '09:00'}" aria-label="From" />
                <span>to</span>
                <input type="time" data-f="end" value="${a.endTime || '17:00'}" aria-label="To" />
              </span>
              <input type="text" class="sch-av-note" data-f="note" maxlength="120" placeholder="Note (optional)" value="${esc(a.note)}" aria-label="Note for ${WEEKDAYS[a.weekday]}" />
            </div>`).join('')}
        </div>
        <div class="sch-dialog-actions">
          <span class="sch-spacer"></span>
          <button type="button" class="sch-btn" id="schAvCancel">Cancel</button>
          <button type="button" class="sch-btn sch-btn--primary" id="schAvSave">Save</button>
        </div>
      </div>`;
    document.body.appendChild(el);
    this._dialog = { el, shift: {} };
    el.querySelectorAll('.sch-seg-btn').forEach((b) => b.addEventListener('click', () => {
      const row = b.closest('.sch-av-edit-row');
      row.querySelectorAll('.sch-seg-btn').forEach((x) => { x.classList.toggle('sch-seg-btn--on', x === b); x.setAttribute('aria-checked', String(x === b)); });
      row.querySelector('.sch-av-times').hidden = b.dataset.st !== 'partial';
    }));
    const close = () => this.closeDialog();
    el.querySelector('.sch-dialog-close').addEventListener('click', close);
    el.querySelector('#schAvCancel').addEventListener('click', close);
    el.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    el.querySelector('#schAvSave').addEventListener('click', () => {
      const days = [...el.querySelectorAll('.sch-av-edit-row')].map((row) => ({
        weekday: Number(row.dataset.wd),
        status: row.querySelector('.sch-seg-btn--on').dataset.st,
        startTime: row.querySelector('[data-f="start"]').value,
        endTime: row.querySelector('[data-f="end"]').value,
        note: row.querySelector('[data-f="note"]').value.trim(),
      }));
      const bad = days.find((d) => d.status === 'partial' && (!d.startTime || !d.endTime || d.startTime === d.endTime));
      if (bad) { el.querySelector(`[data-wd="${bad.weekday}"] [data-f="start"]`).focus(); return; }
      this._handlers.onSaveAvailability?.(staffId, days.map((d) => (d.status === 'partial' ? d : { ...d, startTime: '', endTime: '' })));
    });
  }

  // ── Stations ─────────────────────────────────────────────────────────────────
  _renderStations() {
    const active = this._data.stations.filter((s) => !s.archived);
    this._content.innerHTML = `
      <div class="sch-stations">
        <div class="sch-stations-head">
          <h3>Stations</h3>
          <p>Label each shift with where the person works. Tap a color to change it. Removing a station keeps it on past shifts.</p>
        </div>
        <div class="sch-station-list">
          ${active.map((s) => `
            <div class="sch-station-row" data-id="${esc(s.id)}">
              <button type="button" class="sch-swatch" data-swatch="${esc(s.id)}" style="--sc:${s.color}" aria-label="Change color of ${esc(s.name)}" title="Change color"></button>
              <input type="text" class="sch-station-name" data-name="${esc(s.id)}" value="${esc(s.name)}" maxlength="40" aria-label="Station name" />
              <button type="button" class="sch-station-remove" data-archive="${esc(s.id)}" aria-label="Remove ${esc(s.name)}" title="Remove">&times;</button>
            </div>`).join('') || '<p class="sch-empty">No stations yet. Add your first one below.</p>'}
        </div>
        <div class="sch-station-add">
          <input type="text" id="schNewStationName" maxlength="40" placeholder="New station, e.g. Drive-thru" aria-label="New station name" />
          <button type="button" class="sch-btn sch-btn--primary" id="schAddStation">Add station</button>
        </div>
      </div>`;
  }

  _addStationFromInput() {
    const input = this._content.querySelector('#schNewStationName');
    const name = input?.value.trim();
    if (!name) { input?.focus(); return; }
    const used = this._data.stations.filter((s) => !s.archived).map((s) => s.color);
    const color = STATION_PALETTE.find((c) => !used.includes(c)) ?? STATION_PALETTE[used.length % STATION_PALETTE.length];
    this._handlers.onAddStation?.({ name, color });
  }

  // ── Delegated content events ─────────────────────────────────────────────────
  _onContentClick(e) {
    const t = e.target;
    const week = t.closest('[data-week]');
    if (week) return this._handlers.onWeek?.(Number(week.dataset.week));
    const shiftBtn = t.closest('[data-shift]');
    if (shiftBtn) {
      const sh = this._data.shifts.find((s) => s.id === shiftBtn.dataset.shift);
      if (sh) this.openDialog(sh);
      return;
    }
    const add = t.closest('[data-add-day]');
    if (add) return this.openDialog({ staffId: add.dataset.addStaff || null, day: add.dataset.addDay });
    const go = t.closest('[data-section-go]');
    if (go) {
      this._section = go.dataset.sectionGo;
      this.render(this._data);
      return this._handlers.onSection?.(this._section);
    }
    const review = t.closest('[data-review]');
    if (review) {
      const req = (this._data.requests ?? []).find((r) => r.id === review.dataset.req);
      if (req) this._handlers.onReviewRequest?.(req, review.dataset.review);
      return;
    }
    const avEdit = t.closest('[data-avail-edit]');
    if (avEdit) return this.openAvailabilityDialog(avEdit.dataset.availEdit);
    if (t.closest('#schCopyBtn')) return this._handlers.onCopyLastWeek?.();
    if (t.closest('#schPublishBtn')) return this._handlers.onPublish?.();
    if (t.closest('#schAddStation')) return this._addStationFromInput();
    const sw = t.closest('[data-swatch]');
    if (sw) {
      const st = this._station(sw.dataset.swatch);
      const next = STATION_PALETTE[(STATION_PALETTE.indexOf(st.color) + 1) % STATION_PALETTE.length];
      sw.style.setProperty('--sc', next);
      return this._handlers.onUpdateStation?.(st.id, { color: next });
    }
    const arch = t.closest('[data-archive]');
    if (arch) return this._handlers.onArchiveStation?.(arch.dataset.archive);
  }

  _onContentChange(e) {
    const input = e.target.closest('[data-name]');
    if (!input) return;
    const st = this._station(input.dataset.name);
    const name = input.value.trim();
    if (!name) { input.value = st.name; return; }
    if (name !== st.name) this._handlers.onUpdateStation?.(st.id, { name });
  }
}

export default new ScheduleView();
